// LandingPage.jsx: Manages the main library catalog page layout, including view toggles, and book displays.
import React, { useEffect, useState, useMemo } from 'react';
import { libraryFetch } from '../../libraryFetch';
import { TextField, InputAdornment, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WindowIcon from '@mui/icons-material/Window';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ToggleButton, { toggleButtonClasses } from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';
import ListView from "../BookViews/ListView/ListView";
import GridView from "../BookViews/GridView/GridView";
import BookProfilePage from "../BookProfilePage/BookProfilePage";
import BookCheckout from "../BookCheckout/BookCheckout";
import CheckoutConfirmation from "../CheckoutConfirmation/CheckoutConfirmation";
import Tooltip from '@mui/material/Tooltip';

// defined outside to prevent component re-creating on render
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  gap: '0.7rem',
  [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}`]: {
    borderTopRightRadius: (theme.vars || theme).shape.borderRadius,
    borderBottomRightRadius: (theme.vars || theme).shape.borderRadius,
  },
  [`& .${toggleButtonGroupClasses.lastButton}, & .${toggleButtonGroupClasses.middleButton}`]: {
    borderTopLeftRadius: (theme.vars || theme).shape.borderRadius,
    borderBottomLeftRadius: (theme.vars || theme).shape.borderRadius,
    borderLeft: `1px solid ${(theme.vars || theme).palette.divider}`,
  },
  [`& .${toggleButtonClasses.lastButton}.${toggleButtonClasses.disabled}, & .${toggleButtonGroupClasses.middleButton}.${toggleButtonClasses.disabled}`]: {
    borderLeft: `1px solid ${(theme.vars || theme).palette.action.disabledBackground}`,
  },
}));

const sortOption = [
    { value: 'recentlyAdded', label: 'Recently Added' },
    { value: 'author', label: 'Author' },
    { value: 'title', label: 'Title' },
    { value: 'publicationDate', label: 'Publication Date' }
];

const availabilityOption = [
    { value: '', label: 'All Availabilities' },
    { value: 'available', label: 'Available' },
    { value: 'notAvailable', label: 'Checked Out' }
];

const genreOption = [
    { value: '', label: 'All Genres' },
    { value: 'fiction', label: 'Fiction' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'thriller', label: 'Thriller' },
    { value: 'fantasy', label: 'Fantasy' },
    { value: 'horror', label: 'Horror' },
    { value: 'supernatural', label: 'Supernatural' },
    { value: 'romance', label: 'Romance' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'historicalFiction', label: 'Historical Fiction' },
    { value: 'biography', label: 'Biography' },
    { value: 'memoir', label: 'Memoir' },
    { value: 'history', label: 'History' },
    { value: 'military', label: 'Military' },
    { value: 'politics', label: 'Politics' },
    { value: 'business', label: 'Business' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'finance', label: 'Finance' },
    { value: 'economics', label: 'Economics' },
    { value: 'psychology', label: 'Psychology' },
    { value: 'selfHelp', label: 'Self-Help' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'religion', label: 'Religion' },
    { value: 'philosophy', label: 'Philosophy' },
    { value: 'science', label: 'Science' },
    { value: 'technology', label: 'Technology' },
    { value: 'medicine', label: 'Medicine and Health' },
    { value: 'law', label: 'Law' },
    { value: 'socialScience', label: 'Social Sciences' },
    { value: 'art', label: 'Art' },
    { value: 'media', label: 'Media' },
    { value: 'poetry', label: 'Poetry' },
    { value: 'humor', label: 'Humor' },
    { value: 'travel', label: 'Travel' },
    { value: 'environment', label: 'Environment and Nature' },
    { value: 'education', label: 'Education and Reference' },
    { value: 'anthology', label: 'Anthology' },
    { value: 'trueCrime', label: 'True Crime' }
];

export default function LandingPageLibrary() {
    const [viewMode, setViewMode] = useState('grid');
    const [selectedAvailability, setSelectedAvailability] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('');
    const [selectedSort, setSelectedSort] = useState('recentlyAdded');
    const [searchQuery, setSearchQuery] = useState('');

    const [books, setBooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedBook, setSelectedBook] = useState(null);
    const [showCheckout, setShowCheckout] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    // API base URL comes from the existing React environment configuration.
    const API_BASE = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const loadBooks = async () => {
            setIsLoading(true);
            setError('');

            try {
                const response = await libraryFetch(`${API_BASE}/api/library/books`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to load books: ${response.status}`);
                }

                const data = await response.json();

                setBooks(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to load library books:', err);
                setError('Failed to load books.');
                setBooks([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadBooks();
    }, [API_BASE]);

    const processedRows = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        const filteredRows = books.filter((row) => {
            const title = row.title || '';
            const author = row.author || '';
            const genre = row.genre || '';

            const matchesSearch =
                !query ||
                title.toLowerCase().includes(query) ||
                author.toLowerCase().includes(query);

            const matchesAvailability =
                !selectedAvailability ||
                (selectedAvailability === 'available' && row.isAvailable === true) ||
                (selectedAvailability === 'notAvailable' && row.isAvailable === false);

            let matchesGenre = true;

            if (selectedGenre) {
                const selectedGenreLabel = genreOption.find(
                    (opt) => opt.value === selectedGenre
                )?.label;

                matchesGenre =
                    genre.toLowerCase() === selectedGenreLabel?.toLowerCase();
            }

            return matchesAvailability && matchesGenre && matchesSearch;
        });

        return [...filteredRows].sort((a, b) => {
            switch (selectedSort) {
                case 'author':
                    return (a.author || '').localeCompare(b.author || '');

                case 'title':
                    return (a.title || '').localeCompare(b.title || '');

                case 'publicationDate':
                    return Number(a.Published || 0) - Number(b.Published || 0);

                case 'recentlyAdded':
                default:
                    return Number(a.id || 0) - Number(b.id || 0);
            }
        });
    }, [
        books,
        selectedAvailability,
        selectedGenre,
        selectedSort,
        searchQuery
    ]);

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewMode(nextView);
        }
    };

    const handleBookSelect = async (book) => {
        try {
            const response = await libraryFetch(`${API_BASE}/api/library/books/${book.id}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load book: ${response.status}`);
            }

            const fullBook = await response.json();
            setSelectedBook(fullBook);
        } catch (err) {
            console.error('Failed to load book:', err);

            // Use the book already returned from the list if the
            // individual book request fails.
            setSelectedBook(book);
        }
    };

    const handleCloseProfile = () => {
        setSelectedBook(null);
        setShowCheckout(false);
    };

    const handleCheckout = () => {
        setShowCheckout(true);
    };

    const handleCancelCheckout = () => {
        setShowCheckout(false);
    };

    const handleCheckoutComplete = async () => {
        setShowCheckout(false);
        setShowConfirmation(true);

        // Refresh the book list so the availability shown in the
        // library comes from Salesforce after checkout.
        try {
            const response = await libraryFetch(`${API_BASE}/api/library/books`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBooks(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('Failed to refresh books after checkout:', err);
        }
    };

    const handleConfirmationClose = () => {
        setShowConfirmation(false);
        setSelectedBook(null);
    };

    if (selectedBook && showConfirmation) {
        return (
            <CheckoutConfirmation
                book={selectedBook}
                onClose={handleConfirmationClose}
            />
        );
    }

    if (selectedBook && showCheckout) {
        return (
            <BookCheckout
                book={selectedBook}
                onCancel={handleCancelCheckout}
                onComplete={handleCheckoutComplete}
            />
        );
    }

    if (selectedBook) {
        return (
            <BookProfilePage
                book={selectedBook}
                onClose={handleCloseProfile}
                onCheckout={handleCheckout}
            />
        );
    }

    return (
        <div data-testid="LandingPage" style={{ height: "100%" }}>
            <div className="homePage" data-testid="library-home-page">

                <h1 className="pageTitle">Library</h1>
                <h4 className="pageInfo">
                    Browse, search, and manage books in the library.
                </h4>

                <div className="filterSection" data-testid="book-filter-section">

                    {/* search, filter, and view change section */}
                    <div
                        className="bookSearch-input-field"
                        data-testid="book-search-input-field"
                    >
                        {/* search bar */}
                        <TextField
                            fullWidth
                            placeholder="Search books by title or author"
                            variant="outlined"
                            size="normal"
                            className="bookSearch"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>

                    <div
                        className="genreDropdown-field"
                        data-testid="genre-dropdown-field"
                    >
                        {/* genre dropdown */}
                        <TextField
                            className="genreDropdown"
                            select
                            fullWidth
                            label="Genre"
                            value={selectedGenre}
                            onChange={(e) => setSelectedGenre(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    if (!selected || selected === '') {
                                        return <span>All Genres</span>;
                                    }

                                    const found = genreOption.find(
                                        (opt) => opt.value === selected
                                    );

                                    return found ? found.label : selected;
                                }
                            }}
                        >
                            {genreOption.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    <div
                        className="availabilityDropdown-field"
                        data-testid="availability-dropdown-field"
                    >
                        {/* availability dropdown */}
                        <TextField
                            className="availabilityDropdown"
                            select
                            fullWidth
                            label="Availability"
                            value={selectedAvailability}
                            onChange={(e) => setSelectedAvailability(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{
                                'data-testid': 'availability-select-input',
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    const found = availabilityOption.find(
                                        (opt) => opt.value === selected
                                    );

                                    return found ? found.label : selected;
                                }
                            }}
                        >
                            {availabilityOption.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    <div
                        className="bookView-toggle"
                        data-testid="book-view-toggle"
                    >
                        {/* view switcher */}
                        <StyledToggleButtonGroup
                            exclusive
                            aria-label="book view"
                            size="large"
                            value={viewMode}
                            onChange={handleViewChange}
                        >
                        <Tooltip title="Grid View" placement="top" arrow enterDelay={500} enterNextDelay={500} data-testid="grid-view-tooltip">
                            <ToggleButton value="grid" aria-label="grid view">
                                <WindowIcon />
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title="List View" placement="top" arrow enterDelay={500} enterNextDelay={500} data-testid="list-view-tooltip">

                            <ToggleButton value="list" aria-label="list view">
                                <TableRowsIcon />
                            </ToggleButton>
                        </Tooltip>
                        </StyledToggleButtonGroup>
                    </div>
                </div>

                <div
                    className="currentViewInfo"
                    data-testid="current-view-info"
                >
                    <div
                        className="bookCount-container"
                        data-testid="number-of-books"
                    >
                        <p className="currentBookCount">
                            <b>All Books ({processedRows.length})</b>
                        </p>
                    </div>

                    <div
                        className="sortDropdown-container"
                        data-testid="sort-dropdown"
                    >
                        <p className="sortByText">Sort by: &nbsp;</p>

                        <TextField
                            className="sortByDropdown"
                            select
                            value={selectedSort}
                            onChange={(e) => setSelectedSort(e.target.value)}
                            SelectProps={{
                                MenuProps: {
                                    disableScrollLock: true,
                                }
                            }}
                        >
                            {sortOption.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>
                </div>

                {isLoading && (
                    <p>Loading books...</p>
                )}

                {!isLoading && error && (
                    <p>{error}</p>
                )}

                {!isLoading && !error && processedRows.length === 0 && (
                    <p>No books found.</p>
                )}

                {!isLoading && !error && processedRows.length > 0 && (
                    <>
                        {viewMode === 'list' && (
                            <ListView
                                books={processedRows}
                                onBookSelect={handleBookSelect}
                            />
                        )}

                        {viewMode === 'grid' && (
                            <GridView
                                books={processedRows}
                                onBookSelect={handleBookSelect}
                            />
                        )}
                    </>
                )}

            </div>
        </div>
    );
}